import { useState, useRef, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Progress } from '@/components/ui/progress'
import { TaskStep, TaskStatus, TaskArtifact } from '@/types/entities'
import { TaskStatusBadge } from './TaskStatusBadge'
import { 
  getTaskTypeLabel, 
  useTaskDetails, 
  useTaskArtifacts, 
  useCancelTask, 
  useRetryTask,
  getArtifactTypeLabel,
  formatFileSize
} from '../hooks/useTasks'
import { taskApi } from '../api/taskApi'
import { env } from '@/config/env'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'
import { 
  Loader2, 
  CheckCircle, 
  XCircle, 
  Clock, 
  RotateCcw, 
  ExternalLink,
  AlertTriangle,
  Image as ImageIcon,
  StopCircle,
  Zap
} from 'lucide-react'

interface TaskDetailDialogProps {
  taskId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

function StepStatusIcon({ status }: { status: TaskStatus }) {
  switch (status) {
    case 'RUNNING':
      return <Loader2 className="h-4 w-4 animate-spin text-primary" />
    case 'COMPLETED':
      return <CheckCircle className="h-4 w-4 text-green-600" />
    case 'FAILED':
      return <XCircle className="h-4 w-4 text-destructive" />
    case 'PENDING':
      return <Clock className="h-4 w-4 text-muted-foreground" />
    case 'CANCELLED':
      return <XCircle className="h-4 w-4 text-muted-foreground" />
    default:
      return null
  }
}

interface StepProgress {
  current: number
  total: number
  generated: number
  failed: number
}

function StepProgressBar({ progress }: { progress: StepProgress }) {
  const percentage = progress.total > 0 ? (progress.current / progress.total) * 100 : 0
  
  return (
    <div className="mt-2 space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">
          Generating images: {progress.current} / {progress.total}
        </span>
        <span className="font-medium">
          {Math.round(percentage)}%
        </span>
      </div>
      <Progress value={percentage} className="h-2" />
      <div className="flex gap-3 text-xs text-muted-foreground">
        <span className="text-green-600">✓ {progress.generated} success</span>
        {progress.failed > 0 && (
          <span className="text-destructive">✗ {progress.failed} failed</span>
        )}
      </div>
    </div>
  )
}

// Check if task is running in limited/dev mode by looking at fetch_artwork step
function getTaskLimitedModeInfo(steps: TaskStep[]): { isLimited: boolean; message?: string } {
  const fetchStep = steps.find(s => s.stepName === 'fetch_artwork')
  if (fetchStep?.details?.limitedMode) {
    return {
      isLimited: true,
      message: fetchStep.details.limitedModeMessage as string | undefined
    }
  }
  return { isLimited: false }
}

function TaskStepList({ steps }: { steps: TaskStep[] }) {
  if (!steps || steps.length === 0) {
    return <p className="text-muted-foreground text-sm">No steps recorded.</p>
  }

  return (
    <div className="space-y-3">
      {steps.map((step) => {
        // Check if step has progress data
        const progress = step.details?.progress as StepProgress | undefined
        // Filter out progress and limited mode fields from displayed details
        const fieldsToHide = ['progress', 'limitedMode', 'limitedModeMessage', 'limitedModeConfig']
        const displayDetails = step.details 
          ? Object.fromEntries(Object.entries(step.details).filter(([k]) => !fieldsToHide.includes(k)))
          : null
        const hasDisplayDetails = displayDetails && Object.keys(displayDetails).length > 0

        return (
          <div
            key={step.id}
            className="flex items-start gap-3 p-3 rounded-lg bg-muted/50"
          >
            <div className="flex-shrink-0 mt-0.5">
              <StepStatusIcon status={step.status} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">{step.stepLabel}</span>
                <Badge variant="outline" className="text-xs">
                  Step {step.stepOrder}
                </Badge>
              </div>
              {step.startedAt && (
                <p className="text-xs text-muted-foreground mt-1">
                  Started: {format(new Date(step.startedAt), 'HH:mm:ss')}
                  {step.completedAt && (
                    <> - Completed: {format(new Date(step.completedAt), 'HH:mm:ss')}</>
                  )}
                </p>
              )}
              {/* Progress bar for running steps */}
              {step.status === 'RUNNING' && progress && (
                <StepProgressBar progress={progress} />
              )}
              {step.errorMessage && (
                <div className="mt-2 p-2 bg-destructive/10 rounded text-xs text-destructive">
                  <AlertTriangle className="h-3 w-3 inline mr-1" />
                  {step.errorMessage}
                </div>
              )}
              {hasDisplayDetails && (
                <div className="mt-2 text-xs text-muted-foreground">
                  <pre className="bg-background p-2 rounded overflow-x-auto">
                    {JSON.stringify(displayDetails, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/**
 * Lazy loading image component with placeholder
 * Fetches through API client to include auth headers
 */
function LazyArtifactImage({ taskId, artifact }: { taskId: string; artifact: TaskArtifact }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [isInView, setIsInView] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const imgRef = useRef<HTMLDivElement>(null)

  // Use IntersectionObserver for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      { rootMargin: '100px' } // Load slightly before entering viewport
    )

    if (imgRef.current) {
      observer.observe(imgRef.current)
    }

    return () => observer.disconnect()
  }, [])

  // Fetch image through API client when in view
  useEffect(() => {
    if (!isInView || blobUrl || isLoading || hasError) return

    const fetchImage = async () => {
      setIsLoading(true)
      try {
        const response = await fetch(
          `${env.apiUrl}${taskApi.getArtifactPreviewUrl(taskId, artifact.id)}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth-storage') ? JSON.parse(localStorage.getItem('auth-storage') || '{}')?.state?.authToken : ''}`
            }
          }
        )
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
        
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        setBlobUrl(url)
      } catch (err) {
        console.error('Failed to load artifact preview:', err)
        setHasError(true)
      } finally {
        setIsLoading(false)
      }
    }

    fetchImage()
  }, [isInView, taskId, artifact.id, blobUrl, isLoading, hasError])

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
      }
    }
  }, [blobUrl])

  return (
    <div 
      ref={imgRef}
      className="aspect-square mb-2 rounded bg-background flex items-center justify-center overflow-hidden relative"
    >
      {!isInView ? (
        // Placeholder before scrolling into view
        <ImageIcon className="h-8 w-8 text-muted-foreground" />
      ) : hasError ? (
        // Error state
        <div className="flex flex-col items-center gap-1 text-muted-foreground">
          <XCircle className="h-6 w-6" />
          <span className="text-[10px]">Failed</span>
        </div>
      ) : isLoading || !blobUrl ? (
        // Loading spinner
        <div className="flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        // Loaded image
        <img
          src={blobUrl}
          alt={artifact.name}
          className="w-full h-full object-cover"
        />
      )}
    </div>
  )
}

function TaskArtifactList({ artifacts, taskId }: { artifacts: TaskArtifact[]; taskId: string }) {
  if (!artifacts || artifacts.length === 0) {
    return <p className="text-muted-foreground text-sm">No artifacts generated.</p>
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {artifacts.map((artifact) => {
        // publicUrl contains GCS console URL, or build from gcsPath as fallback
        const consoleUrl = artifact.publicUrl || 
          (artifact.gcsPath ? `https://console.cloud.google.com/storage/browser/_details/arthaus-cloud-dev/${artifact.gcsPath}` : null)
        
        return (
          <div
            key={artifact.id}
            className="border rounded-lg p-2 bg-muted/30 hover:bg-muted/50 transition-colors"
          >
            {/* Lazy-loaded thumbnail */}
            <LazyArtifactImage taskId={taskId} artifact={artifact} />
            
            {/* Details */}
            <div className="space-y-1">
              <p className="text-xs font-medium truncate" title={artifact.name}>
                {artifact.name}
              </p>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{formatFileSize(artifact.fileSize)}</span>
                <Badge variant="outline" className="text-[10px] px-1">
                  {getArtifactTypeLabel(artifact.artifactType)}
                </Badge>
              </div>
              
              {/* GCS Console Link */}
              {consoleUrl && (
                <div className="pt-1">
                  <a
                    href={consoleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" size="sm" className="w-full h-7 text-xs">
                      <ExternalLink className="h-3 w-3 mr-1" />
                      View in GCS
                    </Button>
                  </a>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function TaskDetailDialog({
  taskId,
  open,
  onOpenChange,
}: TaskDetailDialogProps) {
  const { data: task, isLoading } = useTaskDetails(taskId)
  const { data: artifactsData } = useTaskArtifacts(taskId)
  const cancelMutation = useCancelTask()
  const retryMutation = useRetryTask()
  
  const artifacts = artifactsData?.data || task?.artifacts || []

  const handleCancel = async (force = false) => {
    if (task) {
      await cancelMutation.mutateAsync({ taskId: task.id, force })
      onOpenChange(false)
    }
  }

  const handleRetry = async () => {
    if (task) {
      await retryMutation.mutateAsync(task.id)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Task Details</DialogTitle>
          <DialogDescription>
            View the status and progress of this background task.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : task ? (
          <div className="space-y-6">
            {/* Limited Mode Banner */}
            {(() => {
              const limitedInfo = getTaskLimitedModeInfo(task.steps || [])
              if (!limitedInfo.isLimited) return null
              return (
                <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <Zap className="h-4 w-4 text-amber-600 flex-shrink-0" />
                  <span className="text-sm text-amber-700 dark:text-amber-400">
                    {limitedInfo.message || '⚡ Dev mode: Limited variants for faster local testing'}
                  </span>
                </div>
              )
            })()}

            {/* Task Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Type</p>
                <p className="text-sm">{getTaskTypeLabel(task.type)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Status</p>
                <TaskStatusBadge status={task.status} />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Artwork</p>
                {task.artworkId ? (
                  <Link
                    to={`/app/artworks/${task.artworkId}`}
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                    onClick={() => onOpenChange(false)}
                  >
                    {task.artworkName || `#${task.artworkId}`}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                ) : (
                  <p className="text-sm text-muted-foreground">N/A</p>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Retries</p>
                <p className="text-sm">
                  {task.retryCount} / {task.maxRetries}
                </p>
              </div>
            </div>

            <Separator />

            {/* Timeline */}
            <div>
              <p className="text-sm font-medium mb-2">Timeline</p>
              <div className="text-sm space-y-1">
                <p>
                  <span className="text-muted-foreground">Created:</span>{' '}
                  {format(new Date(task.createdAt), 'PPpp')}
                </p>
                {task.startedAt && (
                  <p>
                    <span className="text-muted-foreground">Started:</span>{' '}
                    {format(new Date(task.startedAt), 'PPpp')}
                  </p>
                )}
                {task.completedAt && (
                  <p>
                    <span className="text-muted-foreground">Completed:</span>{' '}
                    {format(new Date(task.completedAt), 'PPpp')}
                  </p>
                )}
              </div>
            </div>

            {/* Error Message */}
            {task.errorMessage && (
              <>
                <Separator />
                <div>
                  <p className="text-sm font-medium mb-2 text-destructive flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4" />
                    Error
                  </p>
                  <div className="p-3 bg-destructive/10 rounded-lg text-sm text-destructive">
                    {task.errorMessage}
                  </div>
                </div>
              </>
            )}

            <Separator />

            {/* Steps */}
            <div>
              <p className="text-sm font-medium mb-3">Progress Steps</p>
              <TaskStepList steps={task.steps || []} />
            </div>

            {/* Artifacts */}
            {artifacts.length > 0 && (
              <>
                <Separator />
                <div>
                  <p className="text-sm font-medium mb-3 flex items-center gap-2">
                    <ImageIcon className="h-4 w-4" />
                    Generated Artifacts ({artifacts.length})
                  </p>
                  <TaskArtifactList artifacts={artifacts} taskId={task.id} />
                </div>
              </>
            )}

            {/* Actions */}
            {(task.status === 'PENDING' || task.status === 'RUNNING' || task.status === 'FAILED') && (
              <>
                <Separator />
                <div className="flex justify-end gap-2">
                  {task.status === 'RUNNING' && (
                    <>
                      <Button
                        variant="destructive"
                        onClick={() => handleCancel(false)}
                        disabled={cancelMutation.isPending}
                      >
                        {cancelMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <StopCircle className="h-4 w-4 mr-2" />
                        )}
                        Stop Task
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleCancel(true)}
                        disabled={cancelMutation.isPending}
                        className="border-orange-500 text-orange-600 hover:bg-orange-50"
                        title="Use when task is stuck and normal stop doesn't work"
                      >
                        {cancelMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <XCircle className="h-4 w-4 mr-2" />
                        )}
                        Force Stop
                      </Button>
                    </>
                  )}
                  {task.status === 'PENDING' && (
                    <Button
                      variant="destructive"
                      onClick={() => handleCancel(false)}
                      disabled={cancelMutation.isPending}
                    >
                      {cancelMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <XCircle className="h-4 w-4 mr-2" />
                      )}
                      Cancel Task
                    </Button>
                  )}
                  {task.status === 'FAILED' && task.retryCount < task.maxRetries && (
                    <Button
                      variant="default"
                      onClick={handleRetry}
                      disabled={retryMutation.isPending}
                    >
                      {retryMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <RotateCcw className="h-4 w-4 mr-2" />
                      )}
                      Retry Task
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Task not found
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
